'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function KacYasindaBotoksYapmaliyimBotoksaBaslamaYasiRehberiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/kac-yasinda-botoks-yapmaliyim-botoksa-baslama-yasi-rehberi';
  const pageTitle = 'Kaç Yaşında Botoks Yapmalıyım? — Botoksa Başlama Yaşı Rehberi';
  const pageDescription = 'Botoksa başlamak için ideal yaş kaçtır? Önleyici botoks nedir, hangi yaşta yapılmalıdır? Kırışıklıkların oluşumuna göre yaş bazlı öneriler.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Kaç Yaşında Botoks Yapmalıyım?', href: '/tr/blog/kac-yasinda-botoks-yapmaliyim-botoksa-baslama-yasi-rehberi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoksa başlama yaşı ve yaşlanma karşıtı uygulamalar değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Botoks Yaşı
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Kaç Yaşında Botoks Yapmalıyım? — Botoksa Başlama Yaşı Rehberi
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>28 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>11 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks (botulinum toksini), yüz mimik kaslarının kasılmasını geçici olarak durdurarak kırışıklıkların oluşumunu engelleyen en etkili estetik işlemlerden biridir. Ancak birçok kişi şu soruyu sorar: "Botoksa kaç yaşında başlanmalı?"
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Cevap, kişinin cilt yapısı, mimik yoğunluğu ve genetik yatkınlığına göre değişir. Genel olarak 25–30 yaş arası dönem, "önleyici botoks" için ideal başlangıç zamanıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Botoksa Erken Başlamanın Amacı
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                "Önleyici botoks", kırışıklıklar derinleşmeden yapılan koruyucu uygulamadır. Yani amaç, oluşmuş çizgileri silmek değil, çizgilerin derinleşmesini engellemektir.
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Faydaları:</h3>
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Kırışıklıklar oluşmadan kas aktivitesi kontrol altına alınır</li>
                  <li>Cilt elastikiyeti uzun süre korunur</li>
                  <li>Gelecekte daha az botoksa ihtiyaç duyulur</li>
                </ul>
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-800 font-medium">
                    💡 Kısacası: Erken yaşta az doz, ilerleyen yaşta yüksek doz yerine geçer.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Yaşa Göre Botoks Rehberi
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Yaş Aralığı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Botoks Amacı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Uygulama Bölgeleri
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">20–25 yaş</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Önleme değil, gerekirse mimik dengeleme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Alın çizgileri, kaş arası</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">25–30 yaş</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Önleyici botoks dönemi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Alın, kaz ayağı, kaş arası</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">30–40 yaş</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Aktif kırışıklık kontrolü</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Göz çevresi, alın, burun kökü</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">40–50 yaş</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Lifting destekli botoks</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Alın, göz çevresi, ağız çevresi</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">50+ yaş</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kırışıklık azaltma ve yüz gençleştirme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Tüm mimik kas bölgeleri</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Cilt Tipine Göre Değişkenlik
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun başlangıç yaşı yalnızca takvime göre değil, cilt yapısına göre belirlenmelidir:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Erken Başlama:</h3>
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>İnce ve açık tenli kişilerde çizgiler daha erken oluşur → 25 yaş civarı başlanabilir</li>
                    <li>Yoğun mimik kullanan kişiler (örneğin öğretmen, oyuncu, sunucu) daha erken yaşta başlayabilir</li>
                  </ul>
                </div>

                <div className="bg-orange-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-orange-900 mb-3">Geç Başlama:</h3>
                  <ul className="list-disc list-inside text-orange-800 space-y-2">
                    <li>Kalın ve yağlı cilt tiplerinde kırışıklıklar daha geç belirir → 30 yaş sonrası uygundur</li>
                    <li>Genetik olarak geç yaşlanan ailelerde daha geç başlanabilir</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Botoksu Ertelemek Neden Sorun Olabilir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Kırışıklıklar derinleştikten sonra, sadece botoks yeterli olmayabilir. Bu durumda dolgu, mezoterapi veya lazer gibi ek işlemler gerekebilir. Bu nedenle koruyucu dönemde yapılan botoks, ilerleyen yaşlarda cerrahiye olan ihtiyacı azaltır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Ne Kadar Sürede Tekrarlanmalı?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun etkisi ortalama 4–6 ay sürer. Önleyici dönemde yılda 2 kez, daha ileri yaşlarda 3 kez uygulanması yeterlidir.
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">Botoksun etkisini uzatmak için:</h3>
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  <li>Güneşten korunmak</li>
                  <li>Bol su içmek</li>
                  <li>Stres ve sigaradan uzak durmak</li>
                </ul>
                <p className="text-yellow-800 mt-3 font-medium">
                  gibi basit önlemler önemlidir.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Botoksa başlama yaşı ve yaşlanma karşıtı uygulamalar"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. 20 yaşında botoks yaptırmak zararlı mı?</h3>
                  <p className="text-gray-700">Hayır, ancak belirgin kırışıklık yoksa genellikle gerek yoktur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Önleyici botoks nedir?</h3>
                  <p className="text-gray-700">Kırışıklıklar oluşmadan önce, düşük dozlarda yapılan koruyucu uygulamadır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Botoksa geç başlamak etkisini azaltır mı?</h3>
                  <p className="text-gray-700">Hayır ama derin kırışıklıkların açılması için daha fazla seans gerekebilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Botoksu erken yaptırmak yüzü tembelleştirir mi?</h3>
                  <p className="text-gray-700">Hayır, kaslar tamamen felç olmaz; sadece kasılma gücü azalır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Erkeklerde botoksa başlama yaşı farklı mı?</h3>
                  <p className="text-gray-700">Evet. Erkeklerin kas yapısı daha güçlü olduğu için genelde 30 yaş civarında başlanır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Dermatology (AAD) – "When to Start Preventive Botox"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Botoks Yaş ve Uygulama Kılavuzu</li>
                <li>Mayo Clinic – "Botox: Safety and Timing"</li>
                <li>Harvard Health Publishing – "Preventive Botox: Facts and Myths"</li>
                <li>ISAPS Global Report 2024 – "Age Demographics in Aesthetic Treatments"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#önleyicibotoks</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#yaşlanmakarşıtı</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#ciltbakımı</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#gençgörünüm</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Botoks Uygulaması İçin Uzman Görüşü Alın</h3>
              <p className="mb-6">Yaşınıza ve cilt tipinize uygun botoks planlaması için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoks uygulaması ve yaş planlaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-green-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-ne-kadar-sure-gider"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">⏱️ Botoks Ne Kadar Süre Gider?</h4>
              <p className="text-gray-600 text-sm">Botoksun etki süresi ve kalıcılık faktörleri hakkında bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-fiyatlari-neden-farkli"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları Neden Farklı?</h4>
              <p className="text-gray-600 text-sm">Botoks fiyat farklılıklarının nedenleri ve kalite faktörleri.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

